/**
 * Application worksheets (v2, FR-C2~C4) — printable HTML views that PREPARE
 * institutional filings. The tool never issues a CO or files a declaration:
 * CEPA CO는 기관발급(UNI-PASS/대한상의), 수출신고는 관세사/유니패스 소관.
 * Research basis: docs/00-research/india-export-requirements.md,
 * korea-export-process.md, taiwan-export-requirements.md.
 */

import type { Partner, PaymentTerm, RuleContext, ShipperProfile } from "@/types";
import { dash, esc, kvTable, printPage } from "./html";

export interface WorksheetInput {
  ctx: RuleContext;
  shipper: ShipperProfile | null;
  paymentTerm?: PaymentTerm | null;
  /** Issued CI doc no (reference on the CO application). */
  ciDocNo?: string | null;
  issuedAt: string; // ISO date
}

function goodsTable(ctx: RuleContext, opts: { hsDigitsNote?: string } = {}): string {
  const lines = ctx.lines.filter((l) => l.item_kind === "product");
  const rows = lines
    .map(
      (l) => `<tr>
<td>${l.line_no}</td><td>${dash(l.description_en)}</td>
<td>${dash(l.hs_code)}</td><td class="num">${l.quantity} ${esc(l.unit)}</td>
<td class="num">${l.unit_price}</td>
<td class="num">${Math.round(l.quantity * l.unit_price * 100) / 100}</td></tr>`,
    )
    .join("");
  return `<table class="cols"><thead><tr>
<th>#</th><th>품명 (Description)</th><th>HS${opts.hsDigitsNote ? ` <span class="badge warn">${esc(opts.hsDigitsNote)}</span>` : ""}</th>
<th>수량</th><th>단가</th><th>금액 (${esc(ctx.deal.currency)})</th></tr></thead>
<tbody>${rows || `<tr><td colspan="6">라인아이템 없음</td></tr>`}</tbody></table>`;
}

function partnerBlock(p: Partner | null | undefined): string {
  if (!p) return "—";
  return `${esc(p.company_name)}<br>${dash(p.address)}<br>${p.country ? esc(p.country) : ""}${
    p.tax_vat_id ? ` · Tax ID: ${esc(p.tax_vat_id)}` : ""
  }`;
}

/** FR-C2 — 한-인도 CEPA CO 기관발급 신청 워크시트. */
export function renderCepaCoWorksheet(input: WorksheetInput): string {
  const { ctx, shipper, ciDocNo, issuedAt } = input;
  const agent = ctx.parties?.authorized_agent ?? null;
  const consignee = ctx.partners.consignee ?? ctx.partners.buyer ?? null;
  const hsUnverified = ctx.products.some((p) => p.hs_code && !p.hs_verified);

  const body = `
<h1>한-인도 CEPA 원산지증명서(CO) 신청 워크시트</h1>
<p class="meta">딜 ${esc(ctx.deal.deal_no)} · 작성일 ${esc(issuedAt)} · 이 문서는 <b>기관발급 신청을 준비하는 내부 워크시트</b>이며 CO 원본이 아닙니다.</p>

<div class="note"><b>발급 경로</b> — 관세청 UNI-PASS(unipass.customs.go.kr) 또는 대한상공회의소(cert.korcham.net).
원칙: 선적일부터 영업일 7일 내 발급 · 소급발급은 1년 내("ISSUED RETROSPECTIVELY" 기재) · <b>CO 유효기간 12개월</b>.</div>

<h2>1. 신청 데이터</h2>
<table>
<tr><th>수출자 (Exporter)</th><td>${shipper ? `${esc(shipper.company_name)}<br>${dash(shipper.address)}<br>사업자번호: ${dash(shipper.business_reg_no)}` : "—"}</td></tr>
<tr><th>생산자 (Producer)</th><td>수출자와 동일 (상이 시 수정 + 원산지확인서 필요)</td></tr>
<tr><th>수입자 (Importer)</th><td>${partnerBlock(consignee)}</td></tr>
<tr><th>인도 공인대리인</th><td>${partnerBlock(agent)}${agent?.importer_ids?.md15_no ? `<br>MD-15: ${esc(agent.importer_ids.md15_no)}` : `<br><span class="badge warn">MD-15 미확인</span>`}</td></tr>
<tr><th>운송 경로</th><td>${dash(shipper?.default_export_port)} → ${dash(ctx.country.name_ko ?? ctx.country.name_en)} (직접운송원칙 — 제3국 경유 시 통과·환적 증빙 필요)</td></tr>
<tr><th>참조 인보이스</th><td>${dash(ciDocNo)}</td></tr>
<tr><th>제3국 송장 여부</th><td>해당 시 CO Box 14에 제3국 사업자 표기</td></tr>
</table>

<h2>2. 품목 및 원산지기준</h2>
${goodsTable(ctx, { hsDigitsNote: "인도는 8자리(HSN)" })}
${hsUnverified ? `<div class="warnbox">⚠ HS코드가 통관사 미확인 상태입니다. <b>HS 세번은 CEPA 양허율·RVC 기준·CDSCO 등급을 동시에 좌우</b>하므로 신청 전 관세사 확정이 필요합니다(9018/9019/8543 분쟁 소지).</div>` : ""}
<table>
<tr><th>원산지결정기준(일반)</th><td><b>CTSH</b>(HS 6단위 세번변경) + <b>RVC 35%</b> 결합기준 — 두 조건 모두 충족 입증. 84·85류 분류 시 품목별 기준(RVC 40~50%) 확인</td></tr>
<tr><th>기준 코드 기재</th><td>WO / PSR / CTSH+RVC 중 해당 코드 (FOB 가격은 RVC 적용 시 기재)</td></tr>
</table>

<h2>3. 구비서류 체크</h2>
<ul class="check">
<li>수출신고필증 사본 ${ctx.deal.export_declaration_no ? `— <span class="badge ok">신고번호 ${esc(ctx.deal.export_declaration_no)}</span>` : `— <span class="badge warn">미기록</span>`}</li>
<li>원산지소명서 (CTSH+RVC 35% 충족 소명)</li>
<li>BOM(자재명세서) + 원재료 단가 자료</li>
<li>제조공정도</li>
<li>송품장(CI)/거래계약서 사본</li>
<li>(생산자≠수출자 시) 원산지확인서</li>
</ul>

<div class="warnbox"><b>CAROTAR 2020</b> — 인도 수입자가 Form I 소명자료(부가가치 산출 근거)를 요구할 수 있고 증빙은 5년 보관 의무입니다.
CO 서식 불완전·유효기간 경과·비대상 품목 발급은 <b>검증 없이 즉시 특혜 부인</b> 사유입니다. CO-CI 정보 불일치는 통관 지연을 부릅니다.</div>`;
  return printPage(`CEPA CO 신청 워크시트 — ${ctx.deal.deal_no}`, body);
}

/** FR-C3 — KCCI 일반(비특혜) CO 신청 워크시트 (대만 등). */
export function renderGeneralCoWorksheet(input: WorksheetInput): string {
  const { ctx, shipper, ciDocNo, issuedAt } = input;
  const consignee = ctx.partners.consignee ?? ctx.partners.buyer ?? null;
  const body = `
<h1>일반(비특혜) 원산지증명서 신청 워크시트</h1>
<p class="meta">딜 ${esc(ctx.deal.deal_no)} · 작성일 ${esc(issuedAt)} · 발급기관: 대한상공회의소(cert.korcham.net)</p>
<div class="note"><b>비특혜 CO</b> — 관세혜택 없음(단순 원산지 증명). ${
    ctx.deal.destination_country === "TW"
      ? "한-대만은 FTA가 없어 특혜 CO가 존재하지 않으며, 대만 통관에서 의료기기는 CO 필수 품목이 아님(바이어·L/C 요청 시 발급)."
      : "바이어·L/C 요청 시 발급."
  }</div>
${kvTable([
    ["수출자 (Exporter)", shipper ? `${shipper.company_name} / ${shipper.address ?? ""}` : ""],
    ["수하인 (Consignee)", consignee ? `${consignee.company_name} / ${consignee.address ?? ""} (${consignee.country})` : ""],
    ["운송수단·경로", `${ctx.deal.transport_mode ?? ""} · ${shipper?.default_export_port ?? ""} → ${ctx.country.name_en}`],
    ["참조 인보이스", ciDocNo],
    ["원산지", "REPUBLIC OF KOREA"],
  ])}
<h2>품목</h2>
${goodsTable(ctx)}
<h2>구비서류 체크</h2>
<ul class="check">
<li>수출신고필증 사본 ${ctx.deal.export_declaration_no ? `— <span class="badge ok">신고번호 ${esc(ctx.deal.export_declaration_no)}</span>` : `— <span class="badge warn">미기록</span>`}</li>
<li>상업송장(CI) 사본</li>
<li>(첫 신청 시) 서명등록·회원가입</li>
</ul>`;
  return printPage(`일반 CO 신청 워크시트 — ${ctx.deal.deal_no}`, body);
}

/** FR-C4 — 관세사 전달용 수출신고 정보 시트. */
export function renderExportDeclarationSheet(input: WorksheetInput): string {
  const { ctx, shipper, paymentTerm, issuedAt } = input;
  const lines = ctx.lines.filter((l) => l.item_kind === "product");
  const totalAmount =
    Math.round(lines.reduce((s, l) => s + l.quantity * l.unit_price, 0) * 100) / 100;
  const totalNet = lines.reduce((s, l) => s + (l.net_weight_kg ?? 0), 0);
  const totalGross = lines.reduce((s, l) => s + (l.gross_weight_kg ?? 0), 0);
  const totalPkg = lines.reduce((s, l) => s + (l.package_count ?? 0), 0);
  const hsUnverified = ctx.products.some((p) => p.hs_code && !p.hs_verified);

  const body = `
<h1>수출신고 정보 시트 (관세사 전달용)</h1>
<p class="meta">딜 ${esc(ctx.deal.deal_no)} · 작성일 ${esc(issuedAt)} · 유니패스 수출신고 위임을 위한 데이터 시트입니다.</p>
${hsUnverified ? `<div class="warnbox">⚠ HS코드 통관사 미확인(hs_verified=false) — 품목분류 확정을 함께 요청하세요.</div>` : ""}
${kvTable([
    ["수출자", shipper ? `${shipper.company_name} (사업자번호 ${shipper.business_reg_no ?? "—"})` : ""],
    ["제조자", "수출자와 동일 (상이 시 수정)"],
    ["목적국", `${ctx.country.name_ko ?? ctx.country.name_en} (${ctx.deal.destination_country})`],
    ["적재항", shipper?.default_export_port],
    ["운송수단", ctx.deal.transport_mode],
    ["인코텀즈", `${ctx.deal.incoterm_id ?? ""} ${ctx.deal.incoterm_named_place ?? ""} (Incoterms® ${ctx.deal.incoterm_version})`],
    ["결제조건", paymentTerm?.label ?? ctx.deal.payment_term_id],
    ["총 금액", `${totalAmount} ${ctx.deal.currency}`],
    ["FOB 원화 환산", ctx.deal.fob_value_krw ? `${ctx.deal.fob_value_krw} KRW` : "(관세사 환산)"],
    ["총 순중량 / 총중량", `${Math.round(totalNet * 1000) / 1000} / ${Math.round(totalGross * 1000) / 1000} kg`],
    ["총 포장 수", totalPkg || ""],
    ["컨테이너 여부", ctx.deal.cargo_type],
  ])}
<h2>품목 (란 구성)</h2>
${goodsTable(ctx, { hsDigitsNote: "수출신고는 HSK 10자리" })}
<div class="note"><b>안내</b> — 수출신고 수리 후 <b>30일 이내 적재</b>해야 합니다. 수리되면 수출신고필증 <b>신고번호를 딜 워크스페이스의 "외부 서류 참조"에 기록</b>하세요(원산지증명 신청 구비서류).
전략물자 해당 여부가 미판정이면 자가판정(전략물자관리원)을 요청하세요.</div>`;
  return printPage(`수출신고 정보 시트 — ${ctx.deal.deal_no}`, body);
}
